import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Alert,
  CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { toast } from "react-toastify";
import useAxios from "../../helpers/Hooks/useAxios";
import { URLS } from "../../helpers/URLS";

const REQUIRED_FIELDS = [
  "Employee Code",
  "Employee Name",
  "Email ID",
  "Mobile",
  "Department",
  "Designation"
];

const OPTIONAL_FIELDS = [
  "Manager Name",
  "Manager Email",
  "Experience",
  "Office Location",
  "Work Location",
  "Country Code",
  "Gender"
];

const BulkUploadModal = ({ open, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);
  const { fetchData } = useAxios();

  const handleReset = () => {
    setFile(null);
    setResults(null);
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClose = () => {
    if (results && results.successCount > 0 && onSuccess) {
      onSuccess();
    }
    handleReset();
    onClose();
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (selected) {
      validateAndSetFile(selected);
    }
  };

  const validateAndSetFile = (selectedFile) => {
    const validExtensions = [".xlsx", ".xls"];
    const fileName = selectedFile.name.toLowerCase();
    const isValid = validExtensions.some((ext) => fileName.endsWith(ext));

    if (!isValid) {
      toast.error("Please upload a valid Excel file (.xlsx or .xls)");
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("File size exceeds 10MB limit");
      return;
    }

    setFile(selectedFile);
    setResults(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = `/api/v1/${URLS.bulkEmployeeTemplate}`;
    link.setAttribute("download", "employee_bulk_upload_template.xlsx");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUpload = async () => {
    if (uploading || !file) return;

    setUploading(true);
    setResults(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const resp = await fetchData({
        url: URLS.bulkUploadEmployees,
        method: "post",
        body: formData,
      });

      if (resp?.status === 200 || resp?.success) {
        setResults(resp.data);
        const { createdCount = 0, updatedCount = 0, failedCount = 0 } = resp.data || {};
        if (createdCount > 0 || updatedCount > 0) {
          toast.success(
            `Import complete: ${createdCount} created, ${updatedCount} updated!`
          );
          if (onSuccess) onSuccess();
        }
        if (failedCount > 0) {
          toast.warn(
            `${failedCount} record(s) could not be processed. See details below.`
          );
        }
      } else {
        toast.error(resp?.message || "Bulk upload failed");
      }
    } catch (err) {
      toast.error(err?.message || "An error occurred during upload");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={uploading ? undefined : handleClose}
      disableEscapeKeyDown={uploading}
      fullWidth
      maxWidth="md"
      className="wh-dialog"
    >
      <div className="wh-dialog-head">
        <div className="wh-dialog-headings">
          <h5>Bulk Employee Upload</h5>
          <p>Import multiple employees at once using an Excel spreadsheet (.xlsx, .xls)</p>
        </div>
        <IconButton
          className="wh-dialog-close"
          aria-label="close"
          onClick={handleClose}
          disabled={uploading}
          sx={{ pointerEvents: uploading ? "none" : "auto" }}
        >
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent sx={{ pt: 2, pb: 2 }}>
        {/* Step 1: Download Template */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            p: 2,
            mb: 2.5,
            borderRadius: "10px",
            backgroundColor: "#f0f7ff",
            border: "1px dashed #b9d9fc",
            opacity: uploading ? 0.6 : 1,
            pointerEvents: uploading ? "none" : "auto",
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1c4f86" }}>
              Download Sample Excel Template
            </Typography>
            <Typography variant="caption" sx={{ color: "#475569" }}>
              Use our standardized Excel template with example rows to ensure seamless import.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            disabled={uploading}
            sx={{
              borderColor: "#2b8fe0",
              color: "#1c4f86",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { borderColor: "#1c4f86", backgroundColor: "#e2effd" },
            }}
          >
            Download Template
          </Button>
        </Box>

        {/* Required & Optional Fields Reference */}
        <Box sx={{ mb: 2.5, opacity: uploading ? 0.6 : 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#334155", display: "block", mb: 0.8 }}>
            Required Columns in Excel:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8, mb: 1.2 }}>
            {REQUIRED_FIELDS.map((f) => (
              <Chip
                key={f}
                label={`${f} *`}
                size="small"
                sx={{
                  backgroundColor: "#fee2e2",
                  color: "#991b1b",
                  fontWeight: 600,
                  fontSize: "11px",
                }}
              />
            ))}
          </Box>

          <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b", display: "block", mb: 0.8 }}>
            Optional Columns (Duplicates will be overwritten):
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
            {OPTIONAL_FIELDS.map((f) => (
              <Chip
                key={f}
                label={f}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: "#cbd5e1",
                  color: "#475569",
                  fontSize: "11px",
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Drop Zone */}
        {!results && (
          <Box
            onDragOver={uploading ? undefined : handleDragOver}
            onDragLeave={uploading ? undefined : handleDragLeave}
            onDrop={uploading ? undefined : handleDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            sx={{
              border: `2px dashed ${isDragOver ? "#2b8fe0" : "#cbd5e1"}`,
              backgroundColor: isDragOver ? "#f0f7ff" : "#f8fafc",
              borderRadius: "12px",
              p: 3.5,
              textAlign: "center",
              cursor: uploading ? "not-allowed" : "pointer",
              pointerEvents: uploading ? "none" : "auto",
              opacity: uploading ? 0.6 : 1,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                borderColor: uploading ? "#cbd5e1" : "#2b8fe0",
                backgroundColor: uploading ? "#f8fafc" : "#f4f9ff",
              },
            }}
          >
            <input
              type="file"
              ref={fileInputRef}
              disabled={uploading}
              onChange={handleFileChange}
              accept=".xlsx, .xls"
              style={{ display: "none" }}
            />
            <CloudUploadIcon sx={{ fontSize: 44, color: isDragOver ? "#2b8fe0" : "#94a3b8", mb: 1 }} />
            <Typography variant="body1" sx={{ fontWeight: 600, color: "#1e293b", mb: 0.5 }}>
              Drag & Drop your Excel file here, or <span style={{ color: "#2b8fe0" }}>browse</span>
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748b" }}>
              Supports .xlsx and .xls files up to 10MB
            </Typography>

            {file && (
              <Box
                sx={{
                  mt: 2,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.2,
                  px: 2,
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <InsertDriveFileIcon sx={{ color: "#0f9d63", fontSize: 24 }} />
                <Box sx={{ textAlign: "left" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                    {file.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    {(file.size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={handleReset}
                  disabled={uploading}
                  sx={{ color: "#ef4444", ml: 1, pointerEvents: uploading ? "none" : "auto" }}
                  title="Remove file"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        )}

        {/* Results View */}
        {results && (
          <Box sx={{ mt: 1 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(4, 1fr)" },
                gap: 1.5,
                mb: 2.5,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.8,
                  textAlign: "center",
                  borderRadius: "10px",
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                }}
              >
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
                  TOTAL ROWS
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#1e293b", mt: 0.5 }}>
                  {results.totalRows || 0}
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 1.8,
                  textAlign: "center",
                  borderRadius: "10px",
                  backgroundColor: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                }}
              >
                <Typography variant="caption" sx={{ color: "#166534", fontWeight: 600 }}>
                  NEWLY ADDED
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#15803d", mt: 0.5 }}>
                  {results.createdCount || 0}
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 1.8,
                  textAlign: "center",
                  borderRadius: "10px",
                  backgroundColor: "#f0f9ff",
                  border: "1px solid #bae6fd",
                }}
              >
                <Typography variant="caption" sx={{ color: "#0369a1", fontWeight: 600 }}>
                  OVERWRITTEN / UPDATED
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#0284c7", mt: 0.5 }}>
                  {results.updatedCount || 0}
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 1.8,
                  textAlign: "center",
                  borderRadius: "10px",
                  backgroundColor: results.failedCount > 0 ? "#fef2f2" : "#f8fafc",
                  border: `1px solid ${results.failedCount > 0 ? "#fecaca" : "#e2e8f0"}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: results.failedCount > 0 ? "#991b1b" : "#64748b", fontWeight: 600 }}
                >
                  FAILED / CONFLICTS
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: results.failedCount > 0 ? "#b91c1c" : "#1e293b",
                    mt: 0.5,
                  }}
                >
                  {results.failedCount || 0}
                </Typography>
              </Paper>
            </Box>

            {results.successCount > 0 && results.failedCount === 0 && (
              <Alert
                icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                severity="success"
                sx={{ mb: 2 }}
              >
                All records processed: {results.createdCount || 0} new employee(s) created, and {results.updatedCount || 0} existing employee(s) overwritten!
              </Alert>
            )}

            {results.failedCount > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#b91c1c", mb: 1 }}>
                  Errors / Conflicts Breakdown ({results.failedCount} row{results.failedCount > 1 ? "s" : ""})
                </Typography>
                <TableContainer
                  component={Paper}
                  sx={{
                    maxHeight: 240,
                    border: "1px solid #fed7aa",
                    borderRadius: "8px",
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: 80 }}>Row #</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 140 }}>Emp Code</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 160 }}>Emp Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Error Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.errors?.map((err, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>
                            Row {err.row}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{err.employeeCode}</TableCell>
                          <TableCell>{err.employeeName}</TableCell>
                          <TableCell sx={{ color: "#dc2626" }}>{err.error}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button
          onClick={handleClose}
          variant="contained"
          color="inherit"
          disabled={uploading}
          sx={{ pointerEvents: uploading ? "none" : "auto" }}
        >
          {results ? "Close" : "Cancel"}
        </Button>
        {results ? (
          <Button
            variant="contained"
            className="wh-primary-btn"
            onClick={handleReset}
          >
            Upload Another File
          </Button>
        ) : (
          <Button
            variant="contained"
            className="wh-primary-btn"
            startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
            onClick={handleUpload}
            disabled={!file || uploading}
            sx={{
              pointerEvents: uploading ? "none" : "auto",
              cursor: uploading ? "not-allowed" : "pointer",
              opacity: uploading ? 0.7 : 1,
            }}
          >
            {uploading ? "Importing..." : "Upload & Import"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkUploadModal;
